import type { components } from "@/api-schema";
import { RelocationOrderTransportMethod } from "@/api-schema-enums";
import { RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS } from "@/components/relocation-order/relocation-order-transport-reason-translations";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { FitUserType, getStatusConfig } from "@/components/relocation-order/_helpers/get-status-config";
import { renderPickupDate } from "@/components/relocation-order/relocation-order-pickup-date-renderer";
import {
  RelocationOrderStatusContainer,
  RelocationOrderStatusContainerVariant,
} from "@/components/relocation-order/relocation-order-status-container";
import { RelationLink } from "@/components/relation-link/relation-link";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import { ClipboardIcon, SettingsIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRelocationOrderDetailAccess } from "../_hooks/use-relocation-order-detail-access";
import { RelocationOrderDetailInfoRow } from "./relocation-order-detail-info-row";

type RelocationOrder = components["schemas"]["RelocationOrder"];

interface RelocationOrderDetailInfoCardProps {
  relocationOrder: RelocationOrder;
}

export function RelocationOrderDetailInfoCard({ relocationOrder }: RelocationOrderDetailInfoCardProps) {
  const { t } = useTranslation(["fit", "common"]);
  const [showSystemInfo, setShowSystemInfo] = useState(false);
  const { canManage, isInternalUser } = useRelocationOrderDetailAccess();

  const userType = isInternalUser ? FitUserType.INTERNAL : FitUserType.EXTERNAL;
  const statusConfig = getStatusConfig(relocationOrder.status, userType, relocationOrder.transportMethod);

  const transportMethodLabel =
    relocationOrder.transportMethod === RelocationOrderTransportMethod.OwnAxle
      ? t("fit:TransportMethodOwnAxle")
      : t("fit:TransportMethodTransportCompany");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardIcon className="h-5 w-5" />
          {t("fit:OrderInformation")}
        </CardTitle>
        {canManage && (
          <CardAction>
            <Button
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
              size={ButtonSize.Sm}
              onClick={() => setShowSystemInfo(!showSystemInfo)}
              className="flex items-center gap-2"
            >
              <SettingsIcon className="h-4 w-4" />
              {showSystemInfo ? t("fit:SystemInformationHide") : t("fit:SystemInformationShow")}
            </Button>
          </CardAction>
        )}
      </CardHeader>

      <CardContent>
        {showSystemInfo && (
          <div className="mb-6">
            <SystemInfoFields
              uuid={relocationOrder?.uuid}
              createdAt={relocationOrder?.createdAt}
              updatedAt={relocationOrder?.updatedAt}
              orientation="horizontal"
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <div>
              <div className="text-muted-foreground text-xs font-medium">{t("fit:TransportStatus")}</div>
              <div>
                <RelocationOrderStatusContainer
                  config={statusConfig}
                  variant={RelocationOrderStatusContainerVariant.COMPACT}
                />
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs font-medium">{t("fit:EarliestPickupDate")}</div>
              <div className="text-sm">
                {renderPickupDate(relocationOrder.earliestPickupDate, relocationOrder.status)}
              </div>
            </div>
            {isInternalUser && (
              <RelocationOrderDetailInfoRow
                label={t("fit:TransportReason")}
                value={
                  relocationOrder.transportReason
                    ? t(RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS[relocationOrder.transportReason])
                    : undefined
                }
              />
            )}
            {isInternalUser && relocationOrder.orderedBy && (
              <div>
                <div className="text-muted-foreground text-xs font-medium">{t("fit:RelocationOrderOrderedBy")}</div>
                <div>
                  <UserItem user={relocationOrder.orderedBy} variant={UserItemVariant.Compact} />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            {isInternalUser && (
              <RelocationOrderDetailInfoRow label={t("fit:TransportMethod")} value={transportMethodLabel} />
            )}
            {isInternalUser && (
              <RelocationOrderDetailInfoRow
                label={t("fit:TransportCompany")}
                value={
                  relocationOrder.transportCompany ? (
                    <RelationLink subject="Company" uuid={relocationOrder.transportCompany.uuid}>
                      {relocationOrder.transportCompany.name}
                    </RelationLink>
                  ) : undefined
                }
              />
            )}
            {isInternalUser && relocationOrder.pickedUpBy && (
              <div>
                <div className="text-muted-foreground text-xs font-medium">{t("fit:PickedUpBy")}</div>
                <div>
                  <UserItem user={relocationOrder.pickedUpBy} variant={UserItemVariant.Compact} />
                </div>
              </div>
            )}
          </div>
        </div>

        {relocationOrder.resolveNote && (
          <div className="border-border mt-4 border-t pt-3">
            <div className="text-muted-foreground text-xs font-medium">{t("fit:SolutionNote")}</div>
            <div className="text-sm">{relocationOrder.resolveNote}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
